import React from 'react';
import classnames from 'classnames';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import gql from 'graphql-tag';

import Icon from 'assets/icons';

import { useSidebarState, useSidebarDispatch } from 'context';

import { ProfileHero, ProfileSidebar } from './';

import { LoaderColumn } from 'components';

import styles from './ProfileSideDrawer.module.scss';

const getCurrentUserQuery = gql`
  query getCurrentUser($userId: String!) {
    me {
      userProfile {
        id
        email
        organization {
          id
          name
          type
        }
      }
    }
    profileByIdentifier(identifier: $userId) {
      ... on Profile {
        organization {
          id
          name
        }
      }
    }
  }
`;

interface ProfileSideDrawerProps {
  showOwned?: boolean;
  toggleOwned?: () => void;
}

export const ProfileSideDrawer: React.FC<ProfileSideDrawerProps> = ({ showOwned, toggleOwned }) => {
  const sidebarState = useSidebarState();
  const sidebarDispatch = useSidebarDispatch();

  const openDrawer = sidebarState.open;

  const closeSidebar = React.useCallback(() => {
    sidebarDispatch({ type: 'CLOSE_SIDEBAR' });
  }, [sidebarDispatch]);

  const toggleOwnedAndClose = () => {
    closeSidebar();
    toggleOwned && toggleOwned();
  };

  return (
    <>
      <div
        className={classnames(styles.overlay, openDrawer && styles.open)}
        onClick={closeSidebar}
      />
      <aside
        className={classnames(
          styles.profileDrawer,
          styles.container,
          openDrawer && styles.openDrawer
        )}
      >
        <div className={styles.innerWrap}>
          <span className={styles.profileClose} onClick={closeSidebar}>
            <Icon.MenuClose />
          </span>
          {openDrawer && (
            <ProfileSideDrawerContent showOwned={showOwned} toggleOwned={toggleOwnedAndClose} />
          )}
        </div>
      </aside>
    </>
  );
};

interface SectionsOptions {
  isCurrentUser: boolean;
  canSeeJobTitle: boolean;
  canEditJobTitle: boolean;
}

export const ProfileSideDrawerContent: React.FC<ProfileSideDrawerProps> = ({
  showOwned,
  toggleOwned,
}) => {
  const { userId = '' } = useParams();
  const { error, data } = useQuery(getCurrentUserQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { userId },
  });

  if (data === undefined) return <LoaderColumn />;

  if (error) return <LoaderColumn />;

  const isCurrentUser = userId === data.me?.userProfile?.id;

  const sectionConfig: SectionsOptions = {
    canEditJobTitle: false,
    canSeeJobTitle: false,
    isCurrentUser: false,
  };

  if (isCurrentUser) {
    sectionConfig.isCurrentUser = true;
  }

  return (
    <>
      <ProfileHero
        canSeeJobTitle={sectionConfig.canSeeJobTitle}
        canEditJobTitle={sectionConfig.canEditJobTitle}
        isCurrentUser={sectionConfig.isCurrentUser}
      />
      <ProfileSidebar />
    </>
  );
};
